/**
 * PROG-03 F1 — FE-side normalizer for legacy precompiled-progression plans.
 *
 * Belt-and-braces complement to the BE normalizer (`fec5c32` on skill-app-be):
 * if the BE somehow returns a plan where the per-set fields on PrecompiledSet
 * are unset (legacy DB row from before the per-set migration, or a migration
 * regression), this FE pass copies each week's week-level params down onto
 * every set in `week.sets`. Idempotent — sets whose per-set field is already
 * set are left untouched.
 *
 * Runs on read only. The save path writes per-set values verbatim; no
 * normalization on save.
 */
import type {
  PrecompiledProgression,
  PrecompiledWeek,
  PrecompiledSet,
} from "../types/Entities";

const PER_SET_KEYS: (keyof PrecompiledSet & keyof PrecompiledWeek)[] = [
  "repSchemeTypeId",
  "repScheme",
  "effortTypeId",
  "effort",
  "tutMode",
  "tut",
  "tecnicaMode",
  "tecnica",
];

function normalizeSet(set: PrecompiledSet, week: PrecompiledWeek): PrecompiledSet {
  let next: PrecompiledSet | null = null;
  for (const k of PER_SET_KEYS) {
    if ((set as any)[k] === undefined || (set as any)[k] === null) {
      const wk = (week as any)[k];
      if (wk !== undefined && wk !== null) {
        if (next === null) next = { ...set };
        (next as any)[k] = wk;
      }
    }
  }
  return next ?? set;
}

export function normalizePrecompiledWeek(week: PrecompiledWeek): PrecompiledWeek {
  const sets = week.sets ?? null;
  if (!sets || sets.length === 0) return week;
  const normalized = sets.map((s) => normalizeSet(s, week));
  const changed = normalized.some((s, i) => s !== sets[i]);
  return changed ? { ...week, sets: normalized } : week;
}

export function normalizePrecompiledProgression(
  prog: PrecompiledProgression
): PrecompiledProgression {
  const weeks = prog.weeks ?? [];
  const normalized = weeks.map(normalizePrecompiledWeek);
  const changed = normalized.some((w, i) => w !== weeks[i]);
  return changed ? { ...prog, weeks: normalized } : prog;
}
